<template>
  <div id="orders-by-position">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title mb-1">
          {{ $t(`views.dashboardDetail.orders.ordersByPosition.title.${type}`) }}
        </h1>
        <v-spacer />
        <ShowCashButton
          v-show="!filters.positionType"
          class="mt-1"
          :showCash="showCash"
          @showCashChanged="showCashChanged"
        />
      </v-card-title>
      <v-card-text class="mt-0 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart v-if="loadedData" :chartData="barChartData" :chartOptions="barChartOptions" />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="orders-by-position-table"
            :headers="headers"
            :items="mySummary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="5"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
          >
            <template slot="body.prepend">
              <tr v-if="totalRow" class="row-total">
                <td>TOTAL</td>
                <template v-if="type === 'scrip'">
                  <td class="text-right">
                    <currency :value="totalRow.positionNumber" />
                  </td>
                </template>
                <template v-else>
                  <td class="text-right">
                    <span />
                  </td>
                  <td class="text-right">
                    <currency :value="totalRow.totalBaseCurrency" :currency="user.baseCurrency" />
                  </td>
                  <td class="text-right">
                    <currency
                      :value="totalRow.totalWithComissionBaseCurrency"
                      :currency="user.baseCurrency"
                      strong
                    />
                  </td>
                </template>
              </tr>
            </template>
            <template v-slot:item.position.ticker="{ item }">
              <div class="items-flex">
                <TableTicker :position="item.position" />
                <template v-if="someNullExchangeRate(item)">
                  <div class="ml-3">
                    <WarningIcon
                      :text="$t(`views.dashboardDetail.orders.ordersByPosition.warning`)"
                    />
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:item.total="{ item }">
              <currency v-if="item._id" :value="item.total" :currency="item.position.currency" />
              <span v-else />
            </template>
            <template v-slot:item.totalBaseCurrency="{ item }">
              <currency :value="item.totalBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.totalWithComissionBaseCurrency="{ item }">
              <currency
                :value="item.totalWithComissionBaseCurrency"
                :currency="user.baseCurrency"
                strong
              />
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { map, get, find } from 'lodash'
import { mapState } from 'vuex'
import { getOrderSummary, getScripSummary } from '@/lib/api.js'
import { DARK_DEFAULT_PALETTE, DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency'
import BarChart from '@/views/components/core/chartjs/BarChart'
import BarLoading from '@/views/components/core/BarLoading'
import TableTicker from '@/views/components/common/TableTicker'
import WarningIcon from '@/views/components/common/WarningIcon.vue'
import ShowCashButton from '@/views/components/common/ShowCashButton'

export default {
  components: { Currency, BarChart, BarLoading, TableTicker, WarningIcon, ShowCashButton },
  props: {
    type: {
      type: String,
      required: true,
    },
    showTable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    mySummary: [],
    totalRow: null,
    currentPage: null,
    loadedData: false,
    barChartData: {
      labels: null,
      datasets: null,
    },
    filters: [],
    showCash: false,
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this
      const isScrip = this.type === 'scrip'
      return {
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: !isScrip ? context.user.baseCurrency : undefined,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: !isScrip ? context.user.baseCurrency : undefined,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
    headers() {
      let headers = [
        {
          text: this.$t('views.orders.ordersTable.ticker'),
          value: 'position.ticker',
          divider: true,
        },
      ]
      if (this.type === 'scrip') {
        headers.push({
          text: `${this.$t('views.orders.ordersTable.positionNumber')}`,
          value: 'positionNumber',
          align: 'right',
        })
      } else {
        headers = headers.concat([
          {
            text: this.$t('views.orders.ordersTable.total'),
            value: 'total',
            align: 'right',
          },
          {
            text: `${this.$t('views.orders.ordersTable.total')} (${getCurrencySymbol(
              this.user.baseCurrency
            )})`,
            value: 'totalBaseCurrency',
            align: 'right',
          },
          {
            text: `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol(
              this.user.baseCurrency
            )})`,
            value: 'totalWithComissionBaseCurrency',
            align: 'right',
          },
        ])
      }
      return headers
    },
  },
  methods: {
    someNullExchangeRate(item) {
      return item.exchangeRates && item.exchangeRates.some((elem) => elem === null)
    },
    itemRowBackground(item) {
      if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    createBarChartObject(data) {
      this.barChartData.datasets = [
        {
          label:
            this.type === 'scrip'
              ? `${this.$t('views.orders.ordersTable.positionNumber')}`
              : `${this.$t('views.orders.ordersTable.totalWithComission')} (${
                  this.user.baseCurrency
                })`,
          data:
            this.type === 'scrip'
              ? map(data, 'positionNumber')
              : map(data, 'totalWithComissionBaseCurrency'),
          backgroundColor: {
            buy: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarBlue
              : DEFAULT_PALETTE.chartsBarBlue,
            sell: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarRed
              : DEFAULT_PALETTE.chartsBarRed,
            scrip: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarBlue
              : DEFAULT_PALETTE.chartsBarBlue,
          }[this.type],
        },
      ]
      this.barChartData.labels = map(data, (item) => {
        if (['fund'].indexOf(item.position.type) > -1 && item.position.name)
          return item.position.name
        return item.position.ticker
      })
    },
    async loadSummary(filters) {
      this.loadedData = false
      this.filters = filters
      try {
        let summary
        if (this.type === 'scrip') {
          summary = await getScripSummary({
            group: 'byPosition',
            filter: {
              ...filters,
            },
          })
        } else {
          summary = await getOrderSummary({
            group: 'byPosition',
            filter: {
              type: this.type,
              positionType: !this.showCash ? { $ne: 'cash' } : undefined,
              isFxOrder: { $ne: true },
              ...filters,
            },
          })
        }

        const mySummary = get(find(summary.data, { type: this.type }), 'positions', []).slice()

        this.totalRow = mySummary.shift()
        this.mySummary = mySummary
        this.currentPage = 1

        this.createBarChartObject(mySummary)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
    showCashChanged(showCash) {
      this.showCash = showCash
      this.loadSummary(this.filters)
    },
  },
}
</script>

<style lang="scss">
#orders-by-position {
  #orders-by-position-table {
    .items-flex {
      display: flex;
    }
  }
}
</style>
